import { SendHorizontal } from "lucide-react";
import { Button, Tag, TextArea } from "~/components/ui";
import { type FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import { MAX_INPUT_LENGTH } from "@noa/ui";
import { isEnterKey, isShiftEnterCommand } from "~/utils/keyboard.utils";
import { QuickPromptsPopoverButton } from "./quick-prompts-popover-button";
import { QuickPrompts } from "./quick-prompts";
import { useSendMessage } from "~/hooks/chat/use-send-message";

export const MessageInput: FunctionComponent = () => {
  const { t } = useTranslation();
  const { content, setContent, canSubmit, handleSendMessage, messageLengthLabel } = useSendMessage();

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Shift+ENTER: add a new line (default behavior)
    if (isShiftEnterCommand(e)) {
      return;
    }

    if (isEnterKey(e)) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div className="flex flex-col gap-2 bg-noa-inset border-t border-noa p-4">
      <div className="relative flex">
        <TextArea
          placeholder={t("chat:messageInputPlaceholder")}
          autoSize={{ minRows: 4, maxRows: 6 }}
          className="pr-20"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          maxLength={MAX_INPUT_LENGTH}
          onKeyDown={handleKeyDown}
        />

        <Tag color="default" className="absolute left-px bottom-px">
          {messageLengthLabel}
        </Tag>

        <QuickPromptsPopoverButton onSelect={setContent} />

        <Button
          variant="primary"
          icon={<SendHorizontal size={14} />}
          disabled={!canSubmit}
          className="absolute right-2 bottom-2 p-0"
          onClick={handleSendMessage}
        />
      </div>

      <QuickPrompts onSelect={setContent} />
    </div>
  );
};
